/**
 * transport/command.ts —— 上行命令信封（CONTRACT §2 上行「命令／音频分片」，同一 WS 连接）。
 * ⚠ 契约缺口留痕：CONTRACT §2 只列「命令/音频分片」未冻结上行帧形状 ⇒ 本件按**最小信封**实装。
 *
 * ★20261005（大王报障「输入框不对」的机制面）：原 `{kind:"command", text}` **不带附件回执与会话归属**
 *   ⇒ Composer 上抛的两参（`files` metas ＋ `sessionId`）在 `useProjection.send` 处被**静默丢弃**，
 *   整条上传链等于白接。现按 102 `emit('submit', {files, sessionId})` 同义落进帧；
 *   ★20261006 F2 订正：`files` 改**最小引用**＝`file_id` 串表（filename/content_type/size **不上线**，
 *   入库＝DB 唯一真相）；空附件 ⇒ 键**不出现**（与旧帧逐字节一致）。
 *
 * ★C-Ⅸ（20261005 夜 · **键对不上账的收口**）：会话归属**线上键＝`target`**（＝后端
 *   `components/entry_session/command_ingress.py::COMMAND_KEYS` 闭集成员；后端 `chat_wiring`
 *   由它定本轮 run 的会话容器）。前端调用面名一律 `sessionId`，**只在本件归一**为 `target`
 *   ——一个会话容器一个键名，线上不再出现 `sessionId`（后端键闭集**越键即拒**）。
 *   ⚠ 附件回执 `files`＝**最小引用**（★20261006 F2 已落地）：`file_id` 串表在键闭集内，
 *     形状校验＝`command_ingress.py::check_files`（越形 ⇒ `E_CMD_FILES` 拒）。
 *
 * ★A-031（20261006 · **会话内容随消息混动**的根因收口）：上行信封增线上键 `client_id`
 *   （＝**客户端关联 id**，后端 `COMMAND_KEYS` 闭集成员，原值随回执回带）。
 *   根因逐字：回执 `{"id":"cmd-2","kind":"command","reply":{…},"status":"accepted"}` **无客户端标识**
 *   ⇒ 前端只能按**文本**匹配「最老的一条 pending」⇒ **连发同文本必错配**、状态串行（＝"内容混动"）。
 *   纪律：`client_id` **仅关联用，不是身份**（身份仍由入口侧验签折算，承「身份永不自报」）；
 *   前端调用面名 `clientId`（`shell/useProjection.ts::send` 用回执行 `localId` 供值），
 *   **只在本件归一**为线上键 `client_id`；空 ⇒ 键**不出现**（旧帧行为逐字节一致）。
 * ★SESS2（20261011 · L-PF-SESS-3）：**会话动作帧**入本件（`buildSessionAction`／`sendSessionAction`）
 *   —— 后端 `SESSION_ACTIONS` 按 `action` 值分派（`command_ingress.py:258-282`），而本件原 `kind`
 *   闭集只有 `command/steer` ⇒ 前端零 `session.switch` 上行点（点存量会话静默）。帧**无 `text` 键**。
 */
import type { WsClient } from "./wsClient";

/** 附件回执 meta（**本地 UI 用**：与 `assembly/input/attachments.ts::metaOf` 同形制） */
export interface CommandFileMeta {
  filename: string;
  content_type: string;
  size: number;
  file_id: string;
}

export interface CommandMeta {
  files?: readonly CommandFileMeta[];
  sessionId?: string;
  /**
   * ★A-031：客户端关联 id（调用面名；线上键＝`client_id`）。
   * 由宿主给出且**唯一、稳定**（`useProjection` 用回执行 `localId`）⇒ 回执按它**精确落位**；
   * 空／缺 ⇒ 线上不写键（不猜关联、后端回带空串）。
   */
  clientId?: string;
  /**
   * ★20261011（大王决策「插话/排队照抄 dsh」）：**投递种类**。
   * · `"command"`（缺省）＝普通提交；
   * · `"steer"` ＝繁忙期**插话**（dsh `resolveSubmitMode ⇒ steer` 同一语义）；
   *   后端受理面已就绪（`runtime/entry/command_ingress.py:47` `KIND_CLOSED_SET=("command","steer","stop")`
   *   ，`:230` 对 `stop|steer` 走同一受理口）⇒ 本件只做**线上归一**。
   * 缺省 ⇒ 键取 `"command"`（旧帧逐字节一致）。
   */
  kind?: "command" | "steer";
}

/** ★SESS2（L-PF-SESS-3）：**会话四动词**（线上键 `action`；后端 `SESSION_ACTIONS` 闭集成员） */
export type SessionAction = "session.switch";

export interface CommandFrame {
  /** ★20261011：`"command"`（缺省）／`"steer"`（插话）；★L-PL-5b：`"stop"`（停止生成）——
   *  三值逐字对齐后端 `KIND_CLOSED_SET=("command","steer","stop")`
   *  （`runtime/entry/command_ingress.py:47`；`stop`＝「停止生成」的同一受理口请求名）。 */
  kind: "command" | "steer" | "stop";
  /**
   * 正文（普通提交／插话恒在；`buildCommand` 逐字节照旧写该键）。
   * ★SESS2：**请求型帧**（`action` 族，如 `session.switch`）**无正文** ⇒ 键**不出现**
   * （后端 `command_ingress.py:258-282` 的会话路只读 `action`/`target`；空载荷不造键）。
   */
  text?: string;
  /**
   * ★SESS2（L-PF-SESS-3 / 方案 §四 B5）：会话动作值（线上键＝`action`，
   * `runtime/entry/command_ingress.py:49-50` `COMMAND_KEYS` 闭集成员）。
   */
  action?: SessionAction;
  /**
   * 附件引用＝**最小引用**（★20261006 自决，承既有定案）：
   *  · 键闭集＝`file_id` **串表**（32 位 hex）——文件名/类型/大小**不随帧走**，
   *    由后端从 `wf.file` 解析（「入库＝DB 唯一真相，双源不允许」＋「引用不复制内容」）；
   *  · 后端形状校验＝`components/entry_session/command_ingress.py::check_files`（越形 ⇒ `E_CMD_FILES` 拒）；
   *  · 空附件 ⇒ 键不出现（空载荷逐字节一致）。
   */
  files?: readonly string[];
  /** 会话归属（提交时刻取值）——**线上键名＝`target`**（后端 `COMMAND_KEYS` 闭集成员） */
  target?: string;
  /**
   * ★A-031：**客户端关联 id**（后端 `COMMAND_KEYS` 闭集成员）——回执原值回带，前端按它落位。
   * **非身份**（不自报身份；会话归属仍由 `target` ＋ 入口侧验签折算）；空 ⇒ 键不出现。
   */
  client_id?: string;
}

/** 构造上行命令（文本提交＝命令；不直连，只经唯一实装通道）
 *  🔴 空 meta / 空 files / 空会话 / 空关联 id ⇒ 不写键（旧行为逐字节一致，判据「空附件 payload 不变」） */
export function buildCommand(text: string, meta?: CommandMeta | null): CommandFrame {
  // ★20261011：插话帧（`kind:"steer"`）与普通提交**同一构造面**（一处归一，消费方不各自拼帧）
  const frame: CommandFrame = { kind: (meta?.kind === "steer" ? "steer" : "command"), text };
  const files = meta && Array.isArray(meta.files) ? meta.files : null;
  // ★最小引用：只上 `file_id` 串表（metas 里的 filename/content_type/size 是本地 UI 面，**不上线**）
  const ids = files ? files.map((f) => f.file_id).filter((id) => typeof id === "string" && id !== "") : [];
  if (ids.length) frame.files = ids;
  const sid = meta && typeof meta.sessionId === "string" ? meta.sessionId : "";
  if (sid !== "") frame.target = sid;                    // ★C-Ⅸ：调用面 `sessionId` → 线上 `target`
  // ★A-031：调用面 `clientId` → 线上 `client_id`（空 ⇒ 不写键；后端回执同值回带）
  const cid = meta && typeof meta.clientId === "string" ? meta.clientId : "";
  if (cid !== "") frame.client_id = cid;
  return frame;
}

/** 经 WS 上行发送命令；未连 ⇒ false（调用方显式处理，不假发） */
export function sendCommand(client: WsClient, text: string, meta?: CommandMeta | null): boolean {
  return client.send(buildCommand(text, meta));
}

/**
 * ★SESS2（L-PF-SESS-3 / 方案 §四 B5）——**会话动作上行帧**（本件新增；此前全树零上行点）。
 *
 * 现状（改动前）：`kind` 闭集只有 `command/steer`，而**后端的会话四动词是按 `action`
 * 值分派**的（`runtime/entry/command_ingress.py:258-282`：`session.switch` ⇒
 * `sessions.switch(actor, target)`）⇒ 前端**发不出该帧**；REST 路亦不可走
 * （`transport/whitelist.ts:50` `assertEgress` 只认逐字路径，`/api/sessions/{id}/select`
 * 出不去）⇒ 唯一合法通道＝本 WS 命令帧。
 *
 * 帧形状（**逐键对齐后端闭集**；**无 `text` 键**）：
 *   `{"kind":"command","action":"session.switch","target":"<会话 id>"[,"client_id":"…"]}`
 * 依据（既有用例逐字）：`components/entry_session/tests/test_entry_session_selfcheck.py:447`
 *   `command_ingress({"kind": "command", "action": "session.switch", "target": "s-1"}, ctx)`。
 * 空会话 id／空关联 id ⇒ 该键**不出现**（旧纪律：空载荷逐字节一致，不猜不补）。
 */
export function buildSessionAction(action: SessionAction, sessionId?: string,
                                   meta?: CommandMeta | null): CommandFrame {
  const frame: CommandFrame = { kind: "command", action };
  const sid = typeof sessionId === "string" && sessionId !== "" ? sessionId
    : (meta && typeof meta.sessionId === "string" ? meta.sessionId : "");
  if (sid !== "") frame.target = sid;
  const cid = meta && typeof meta.clientId === "string" ? meta.clientId : "";
  if (cid !== "") frame.client_id = cid;
  return frame;
}

/** 经 WS 上行发送会话动作；未连 ⇒ false（调用方显式处理，不假发） */
export function sendSessionAction(client: WsClient, action: SessionAction, sessionId?: string,
                                  meta?: CommandMeta | null): boolean {
  return client.send(buildSessionAction(action, sessionId, meta));
}

/**
 * ★L-PL-5b（PL5 前端三缺口）——**停止生成上行帧**（`kind:"stop"`；后端 `KIND_CLOSED_SET`
 * `runtime/entry/command_ingress.py:47` 闭集成员，「停止生成」与 `steer` 同一受理口，
 * 平台事实出口＝`platform.ws.command_accepted`（`src/sononux/decl/baseline.yaml:111` 已声明））。
 * 帧形状（**无 `text` 键**，与 `buildSessionAction` 同纪律）：
 *   `{"kind":"stop","target":"<会话 id>"[,"client_id":"…"]}`
 * 空会话 id／空关联 id ⇒ 该键**不出现**（空载荷不造键，不猜不补）。
 */
export function buildStop(sessionId?: string, meta?: CommandMeta | null): CommandFrame {
  const frame: CommandFrame = { kind: "stop" };
  const sid = typeof sessionId === "string" && sessionId !== "" ? sessionId
    : (meta && typeof meta.sessionId === "string" ? meta.sessionId : "");
  if (sid !== "") frame.target = sid;
  const cid = meta && typeof meta.clientId === "string" ? meta.clientId : "";
  if (cid !== "") frame.client_id = cid;
  return frame;
}

/** 经 WS 上行发送停止生成；未连 ⇒ false（调用方显式处理，不假发） */
export function sendStop(client: WsClient, sessionId?: string, meta?: CommandMeta | null): boolean {
  return client.send(buildStop(sessionId, meta));
}
